import React, { useState, useEffect } from 'react';
import { ArrowRight, PlayCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const modules = [
  {
    id: 1,
    title: 'Module 1: The Call to Consciousness',
    subtitle: 'An Invitation to Awakening',
    image: 'https://horizons-cdn.hostinger.com/18a1a0ab-885d-4c22-add1-e6337891edf7/a13f51a2c0be45427a7d8ab30aeff9bd.jpg'
  },
  {
    id: 2,
    title: 'Module 2: Foundations of Sacred Gender Archetypes',
    subtitle: 'Understanding the Divine Masculine and Feminine',
    image: 'https://horizons-cdn.hostinger.com/18a1a0ab-885d-4c22-add1-e6337891edf7/21f8bc562a513300c48d258682621835.jpg'
  },
  {
    id: 3,
    title: 'Module 3: Ontology of the Masculine and Feminine',
    subtitle: 'Exploring the Depths of Energy',
    image: 'https://horizons-cdn.hostinger.com/18a1a0ab-885d-4c22-add1-e6337891edf7/10c74f18fe2c3508d765dbb95aa54bbc.jpg'
  }
];

const ModuleCard = ({ module, isActive }) => {
  return (
    <div
      className={`relative transition-all duration-500 ease-out rounded-xl overflow-hidden flex-shrink-0 ${
        isActive
          ? 'w-[400px] md:w-[500px] z-10 opacity-100 scale-100'
          : 'w-[280px] md:w-[350px] opacity-60 scale-95'
      }`}
      style={{
        aspectRatio: '16/9',
        boxShadow: isActive ? '0 0 50px rgba(212, 163, 58, 0.4)' : 'none',
        border: isActive ? '3px solid #d4a33a' : '2px solid #3a4152'
      }}
    >
      <img
        src={module.image}
        alt={module.title}
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
        <h3 className={`font-semibold text-[#d4a33a] mb-1 ${isActive ? 'text-sm md:text-base' : 'text-xs'}`}>
          {module.title}
        </h3>
        <p className={`text-gray-300 ${isActive ? 'text-xs md:text-sm' : 'text-xs'}`}>
          {module.subtitle}
        </p>
      </div>
    </div>
  );
};

const HeroSection = () => {
  const { t } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % modules.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % modules.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + modules.length) % modules.length);

  const getModuleOrder = () => {
    const prev = (currentSlide - 1 + modules.length) % modules.length;
    const next = (currentSlide + 1) % modules.length;
    return [
      { module: modules[prev], isActive: false },
      { module: modules[currentSlide], isActive: true },
      { module: modules[next], isActive: false }
    ];
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative bg-[#1a1f2e] pt-20 pb-16 overflow-hidden">
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 md:pt-16">
        {/* Badge */}
        <div className="flex justify-center mb-6">
          <span className="inline-block px-4 py-2 bg-transparent border border-[#d4a33a] text-[#d4a33a] text-xs sm:text-sm font-semibold tracking-wider rounded">
            {t('masterclassAvailable')}
          </span>
        </div>

        {/* Title */}
        <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-center mb-4">
          <span className="text-white">{t('theSacred')} </span>
          <span className="text-[#d4a33a]">{t('energyUnion')}</span>
        </h1>

        {/* Subtitle */}
        <p className="text-gray-400 text-center text-lg md:text-xl mb-8">
          {t('heroSubtitle')}
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
          <button 
            onClick={() => scrollToSection('enroll')}
            className="w-full sm:w-auto bg-[#d4a33a] hover:bg-[#c49530] text-white px-8 py-3.5 rounded flex items-center justify-center gap-2 font-medium transition-all duration-300 hover:shadow-lg hover:shadow-[#d4a33a]/20"
          >
            {t('startYourJourney')}
            <ArrowRight className="w-5 h-5" />
          </button>
          <button 
            onClick={() => scrollToSection('curriculum')}
            className="w-full sm:w-auto bg-transparent border border-gray-600 hover:border-gray-400 text-white px-8 py-3.5 rounded flex items-center justify-center gap-2 font-medium transition-all duration-300"
          >
            <PlayCircle className="w-5 h-5" />
            {t('exploreCurriculum')}
          </button>
        </div>

        {/* Module Carousel - Landscape 16:9 */}
        <div className="relative max-w-6xl mx-auto">
          <div className="flex items-center justify-center">
            {/* Navigation Left */}
            <button
              onClick={prevSlide}
              className="absolute left-0 md:left-4 z-20 w-10 h-10 bg-[#2a3142] hover:bg-[#3a4152] rounded-full flex items-center justify-center text-white transition-colors duration-200"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            {/* Slides */}
            <div className="flex items-center justify-center gap-4 md:gap-6 py-4 overflow-hidden">
              {getModuleOrder().map((item, index) => (
                <ModuleCard
                  key={item.module.id}
                  module={item.module}
                  isActive={item.isActive}
                />
              ))}
            </div>

            {/* Navigation Right */}
            <button
              onClick={nextSlide}
              className="absolute right-0 md:right-4 z-20 w-10 h-10 bg-[#2a3142] hover:bg-[#3a4152] rounded-full flex items-center justify-center text-white transition-colors duration-200"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center gap-2 mt-6">
            {modules.map((m, index) => (
              <button
                key={m.id}
                onClick={() => setCurrentSlide(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentSlide
                    ? 'w-8 bg-[#d4a33a]'
                    : 'w-2 bg-gray-600 hover:bg-gray-500'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
